import { useMemo } from 'react';
import { useRbacState } from '../context/rbac.context';
import {
  getPermission,
  hasReadPermission,
  hasWritePermission,
  hasDeletePermission,
  hasAnyPermission,
} from '../utils/rbacHelpers';
import { IRbacPermission } from '../apis/__shared/rbac.interfaces';

export interface UseRbacPermissionReturn {
  canRead: boolean;
  canWrite: boolean;
  canDelete: boolean;
  hasAny: boolean;
  permission: IRbacPermission | null;
  isLoading: boolean;
}

/**
 * Custom hook to check RBAC permissions for a specific module and submodule
 * @param module - Module name (e.g., 'students', 'staff')
 * @param submodule - Submodule name (e.g., 'studentList', 'attendance')
 * @returns Object with permission flags and permission object
 */
export const useRbacPermission = (
  module: string,
  submodule: string
): UseRbacPermissionReturn => {
  const { rbac, isLoading } = useRbacState();

  return useMemo(() => {
    if (!rbac || isLoading) {
      return {
        canRead: false,
        canWrite: false,
        canDelete: false,
        hasAny: false,
        permission: null,
        isLoading,
      };
    }

    const permission = getPermission(rbac, module, submodule);

    return {
      canRead: hasReadPermission(rbac, module, submodule),
      canWrite: hasWritePermission(rbac, module, submodule),
      canDelete: hasDeletePermission(rbac, module, submodule),
      hasAny: hasAnyPermission(rbac, module, submodule),
      permission,
      isLoading: false,
    };
  }, [rbac, module, submodule, isLoading]);
};
